import { subscriptionPlan } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Subscription Plan Service
 * Handles all subscription plan master API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/subscription-plan/list - Get subscription plan list
 * - GET /api/v1/admin/subscription-plan/get - Get single subscription plan
 * - POST /api/v1/admin/subscription-plan/add - Add new subscription plan
 * - PUT /api/v1/admin/subscription-plan/update - Update subscription plan
 * - PUT /api/v1/admin/subscription-plan/update-status - Toggle status
 * - DELETE /api/v1/admin/subscription-plan/delete - Delete subscription plan
 */
class SubscriptionPlanService {
  /**
   * @name getList
   * @description Fetches subscription plan list from admin API with pagination, search, status and user_type filter.
   * @param {Object} params - Query parameters (page, limit, search, status, user_type)
   * @returns {Promise<Object>} The response data containing the subscription plan list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(subscriptionPlan.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single subscription plan by UUID.
   * @param {string} uuid - Subscription Plan UUID
   * @returns {Promise<Object>} The response data containing subscription plan details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(subscriptionPlan.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new subscription plan.
   * @param {Object} data - Subscription plan data with name, user_type, apple_product_id, google_product_id and is_active
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(subscriptionPlan.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing subscription plan.
   * @param {Object} data - Subscription plan data with uuid, name, user_type, apple_product_id, google_product_id and is_active
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(subscriptionPlan.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name updateStatus
   * @description Toggles the active/inactive status of a subscription plan.
   * @param {string} uuid - Subscription Plan UUID
   * @param {boolean} isActive - New status
   * @returns {Promise<Object>} The response data.
   */
  static async updateStatus(uuid, isActive) {
    try {
      const response = await axios.put(subscriptionPlan.updateStatus, { uuid, is_active: isActive });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes a subscription plan by UUID.
   * @param {string} uuid - Subscription Plan UUID
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(subscriptionPlan.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { SubscriptionPlanService };
